import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  Auth as FireBaseAuth,
  User,
  UserCredential,
  signInWithCustomToken,
  sendEmailVerification,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { firebase } from 'app/firebase';
import { env } from 'config/env';
import { createDeveloperToken, updateAdminUser } from './graphql';
import { isAllowedSignupEmail, SIGNUP_DOMAIN_ERROR } from './emailDomains';
import * as Sentry from '@sentry/react';

export type AuthResponse<TData = undefined> =
  | { ok: false; error: string }
  | { ok: true; data: TData };

interface RegisterProps {
  firstName: string;
  lastName: string;
  email: string;
  website: string;
  password: string;
}

// https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth
const ERROR_MAP: { [key: string]: string } = {
  base: 'Something is wrong. Please try again later.',

  'auth/invalid-email': 'Invalid email.',
  'auth/user-disabled': 'User disabled.',
  'auth/user-not-found': 'User not found.',
  'auth/wrong-password': 'Wrong password.',
  'auth/operation-not-allowed': 'Operation not allowed.',
  'auth/email-already-in-use': 'Email already in use.',
  'auth/weak-password': 'Weak password.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/user-token-expired': 'Your session expired. Please sign in again.',
};

class Auth {
  auth: FireBaseAuth;
  googleProvider: GoogleAuthProvider;

  constructor() {
    firebase.initialize();
    this.auth = getAuth(firebase.app);
    this.googleProvider = new GoogleAuthProvider();
  }

  private logError(err: unknown) {
    Sentry.captureException(err);

    if (env.VITE_NODE_ENV !== 'production') {
      console.error(err);
    }
  }

  private getErrorMessage(error: unknown): string {
    const key = (error as { code?: string } | undefined)?.code;
    return (key && ERROR_MAP[key]) || ERROR_MAP.base;
  }

  // Register with Google account, or sign in if account already exists
  public signInOrRegisterWithGoogle = async (website?: string): Promise<AuthResponse<User>> => {
    try {
      const resp = await signInWithPopup(this.auth, this.googleProvider);

      // Restrict *new* accounts to approved company email domains (UX gate only).
      // Existing users on any domain may still sign in.
      const isNewUser = getAdditionalUserInfo(resp)?.isNewUser ?? false;
      if (isNewUser && !isAllowedSignupEmail(resp.user.email)) {
        // Remove the account Firebase just created for the rejected signup
        await resp.user.delete().catch(() => this.auth.signOut());
        return { ok: false, error: SIGNUP_DOMAIN_ERROR };
      }

      const { firestoreUser, user } = await updateFirestoreUser({
        userCred: resp,
        website,
      });

      if (!firestoreUser) {
        throw new Error('failed to create user');
      }

      return { ok: true, data: user };
    } catch (err) {
      this.logError(err);

      return { ok: false, error: ERROR_MAP.base };
    }
  };

  public logInWithEmailAndPassword = async (
    email: string,
    password: string,
  ): Promise<AuthResponse<User>> => {
    try {
      const resp = await signInWithEmailAndPassword(this.auth, email, password);
      return { ok: true, data: resp.user };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.logError(error);
      const key = error?.code as string;
      return { ok: false, error: ERROR_MAP[key] || ERROR_MAP.base };
    }
  };

  public assumeDeveloperIdentity = async (
    developerId: string,
    apiKey: string | undefined,
    userToken?: string | null,
    readOnly = true,
  ): Promise<AuthResponse<User>> => {
    const response = await createDeveloperToken({
      developerId,
      readOnly,
      apiKey,
      userToken,
    });

    const errorMsg = response?.data?.createDeveloperToken?.error;
    const token = response?.data?.createDeveloperToken?.token;

    if (!token) {
      return { ok: false, error: errorMsg || ERROR_MAP.base };
    }

    try {
      const resp = await signInWithCustomToken(this.auth, token);
      return { ok: true, data: resp.user };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      this.logError(error);
      const key = error?.code as string;
      return { ok: false, error: ERROR_MAP[key] || ERROR_MAP.base };
    }
  };

  // Create / register a new user that signed up with email/password
  public registerWithEmailAndPassword = async ({
    firstName,
    lastName,
    email,
    website,
    password,
  }: RegisterProps): Promise<AuthResponse<User>> => {
    try {
      // Restrict new accounts to approved company email domains (UX gate only)
      if (!isAllowedSignupEmail(email)) {
        return { ok: false, error: SIGNUP_DOMAIN_ERROR };
      }

      // Create new user via Firebase Auth
      const resp = await createUserWithEmailAndPassword(this.auth, email, password);
      const { firestoreUser, user } = await updateFirestoreUser({
        userCred: resp,
        firstName,
        lastName,
        website,
      });

      if (!firestoreUser) {
        throw new Error('Failed to create user');
      }

      return { ok: true, data: user };
    } catch (err) {
      this.logError(err);
      return { ok: false, error: this.getErrorMessage(err) };
    }
  };

  public sendVerificationEmail = async (user: User): Promise<AuthResponse> => {
    try {
      await sendEmailVerification(user, {
        url: window.location.origin,
        handleCodeInApp: false,
      });
      return { ok: true, data: undefined };
    } catch (err) {
      this.logError(err);
      return { ok: false, error: this.getErrorMessage(err) };
    }
  };

  public reloadUser = async (user: User): Promise<AuthResponse<User>> => {
    try {
      await user.reload();
      return { ok: true, data: this.auth.currentUser ?? user };
    } catch (err) {
      this.logError(err);
      return { ok: false, error: this.getErrorMessage(err) };
    }
  };

  public sendPasswordReset = async (email: string): Promise<{ error?: string }> => {
    return await sendPasswordResetEmail(this.auth, email)
      .then(() => {
        alert('Password reset link has been sent');
        return {};
      })
      .catch((err) => {
        this.logError(err);
        const key = err?.code as string;
        return { error: ERROR_MAP[key] || ERROR_MAP.base };
      });
  };

  public logout = () => {
    signOut(this.auth);
  };
}

const authAPI = new Auth();

export default authAPI;
async function updateFirestoreUser({
  userCred,
  firstName,
  lastName,
  website,
}: {
  userCred: UserCredential;
  firstName?: string;
  lastName?: string;
  website?: string;
}) {
  const user = userCred.user;
  const displayName = firstName && lastName ? [firstName, lastName].join(' ') : user.displayName;
  const userToken = await user.getIdToken();
  if (!user.email) {
    throw new Error('User missing email');
  }

  // Create a Firestore record with additional user details
  const mutation = await updateAdminUser({
    updates: {
      ...(firstName && { firstName }),
      ...(lastName && { lastName }),
      ...(displayName && { displayName }),
      ...(website && { website }),
    },
    adminToken: userToken,
  });

  const firestoreUser = mutation?.data?.updateUser?.data?.user;
  return { firestoreUser, userToken, user };
}
